<script setup lang="ts">
/**
 * The line SPEC §9.7 asks for when the connection drops.
 *
 * One sentence, in the specification's own words, and on every screen rather
 * than only the family ones. The organizer's screens could have their own
 * wording, but a second string for the same fact is a second string to keep
 * true, and the installed application — the one most likely to be opened with
 * no signal — is the family's.
 *
 * Announced politely. Losing signal in a car park is not an emergency, and an
 * assertive region interrupts whatever the screen reader was in the middle of
 * saying. That is also why this is not a `v-alert`: Vuetify gives that
 * `role="alert"`, which is assertive whatever it is wrapped in.
 */
import { useAppStore } from '@/stores/app'

const app = useAppStore()
</script>

<template>
  <!--
    The live region is always present and only its text changes. A region
    created at the same moment as its content is not reliably announced.
  -->
  <div class="offline" role="status" aria-live="polite">
    <p v-if="!app.isOnline" class="offline__line" data-testid="offline-notice">
      <v-icon icon="mdi-wifi-off" size="18" aria-hidden="true" />
      You're offline. Reconnect to update your RSVP.
    </p>
  </div>
</template>

<style scoped>
/*
  Fixed, above the app bar, so it is seen whatever screen is showing and never
  pushes the content a thumb was about to tap.
*/
.offline {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000;
  pointer-events: none;
}

.offline__line {
  pointer-events: auto;
  margin: 0;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  /* The sunken surface and its own text colour, not warning-on-wash: that pair
     is the one whose contrast is stated in the theme rather than inferred by
     Vuetify, and MT-114 found what inferring it produced. */
  background: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-on-surface-variant));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
